// 마을 장비 상점 탭(S7, STAGE-WORLD-PRD 5.2): 잡화상(방어구·장신구)·대장간(무기) 창의 「마을 장비」 탭.
// 표시 값은 실제 규칙(content/townShop 품목·가격, shared/gear 능력치)에서 바로 만든다 — 산 개체와 화면 미리보기가 같은 함수(townGearItem)다.
// 구매는 GameCommand 'gearBuy'로만 보낸다(장부가 품목 id로 개체를 다시 만든다). 플레이 화면 위 팝업 없이 RpgUi 창 본문에만 그린다.
import type { GameCommand } from '../shared/types';
import type { GearView } from '../shared/gearView';
import type { HeroId } from '../shared/content/types';
import type { NpcRole } from '../shared/rpg';
import { BAG_CAP } from '../shared/content/loot';
import { offerName, townGearBuyableBy, townGearFor, townGearItem, townGearLevel, townGearPrice } from '../shared/content/townShop';
import { equipDef, itemBaseStats, socketsOf } from '../shared/gear';
import { gearCard } from './gearModel';
import { FONT, el } from './dom';
import { makeRequestId } from './rpgModel';
import { iconOver, townCrest } from './uiArt';
import { itemIcon } from './itemIconDom';
import { townGearIconPath } from './itemIcons';
import { DEFAULT_BODY_W, shopRowLayout } from './shopLayout';
import { cornerTag, priceTag, socketPip } from './shopUi';

const num = (v: number): string => v.toLocaleString('ko-KR');
/** 영웅별 무기 칸이 생기기 전 카엘 외 영웅이 무기를 보면 보이는 이유(구매 버튼은 비활성) */
export const HERO_WEAPON_NOTE = '이 영웅은 아직 무기를 낄 수 없어요';
const TOWN_NAME = ['', '시작 마을', '잿불 전초기지', '서리 항구', '늪지 장터'];

export interface TownGearRow {
  readonly id: string;
  readonly name: string;
  readonly slotName: string;
  readonly gradeName: string;
  readonly color: string;
  readonly ilvl: number;
  readonly stats: readonly string[];
  readonly affixes: readonly string[];
  readonly sockets: string;
  /** 소켓 칸 수(등급 기준, 0이면 소켓 없음) — 소켓 그림 칸을 그린다 */
  /** 소켓 칸별 보석 코드(빈 칸 -1) — 마을③ 목걸이처럼 보석이 박힌 상점 장비도 그대로 그린다 */
  readonly socketCodes: readonly number[];
  readonly desc: string;
  readonly price: number;
  /** 같은 정의를 이미 가진 수(가방 + 장착) */
  readonly owned: number;
  /** 지금 장착한 같은 부위와 비교(예: "장착 중 공격력 +40 → +52"), 빈 칸이면 "빈 칸에 장착 가능" */
  readonly compare: string;
  readonly can: boolean;
  /** 살 수 없는 이유(살 수 있으면 '') */
  readonly reason: string;
  /** 아이콘 그림 경로(assetUrl 전, public/assets/ui/items/gear_t<마을>_<부위>). 없으면 null */
  readonly icon: string | null;
}

/** 그 마을 그 NPC의 장비 품목 줄. 마을 장비가 없는 마을(①)이면 빈 목록 */
export function townGearRows(g: Pick<GearView, 'items' | 'equipped' | 'heroWeapons'>, town: number, seller: NpcRole, hero: HeroId, gold: number, locked: boolean): TownGearRow[] {
  // 가방 수 = 장부 bagCount와 같은 기준: 지금 영웅 장착 + 모든 영웅 무기 칸(hw)을 중복 없이 뺀다(Codex P2 #355 — 다른 영웅 무기를 가방으로 세어 '가방이 가득' 오판)
  const worn = new Set<string>();
  for (const u of [...Object.values(g.equipped), ...Object.values(g.heroWeapons ?? {})]) if (u && g.items.some((it) => it.uid === u)) worn.add(u);
  const bag = g.items.length - worn.size;
  return townGearFor(town, seller).map((o): TownGearRow => {
    const item = townGearItem(o, hero, 'preview:' + o.id);
    const card = gearCard(item, false, hero);
    const price = townGearPrice(o);
    const owned = g.items.filter((it) => it.def === item.def).length;
    const curUid = g.equipped[o.slot];
    const cur = curUid ? g.items.find((it) => it.uid === curUid) : undefined;
    const a = itemBaseStats(item), b = cur ? itemBaseStats(cur) : null;
    const key: 'attack' | 'maxHp' | 'crit' = a.attack ? 'attack' : a.maxHp ? 'maxHp' : 'crit';
    const label = key === 'attack' ? '공격력' : key === 'maxHp' ? '최대 HP' : '치명 확률';
    const fmt = (v: number): string => key === 'crit' ? '+' + Math.round(v * 1000) / 10 + '%' : '+' + num(v);
    const compare = !townGearBuyableBy(o, hero) ? '이 영웅은 아직 무기를 낄 수 없어요' : !cur ? '빈 칸에 장착할 수 있어요' : `장착 중 ${equipDef(cur.def)?.name ?? '장비'}: ${label} ${fmt(b![key])} → ${fmt(a[key])}`;
    const reason = locked ? '지금은 거래할 수 없어요' : !townGearBuyableBy(o, hero) ? HERO_WEAPON_NOTE : bag >= BAG_CAP ? '가방이 가득 찼어요' : gold < price ? '골드 부족 · ' + num(price - gold) + ' 골드 필요' : '';
    return {
      id: o.id, name: offerName(o, hero), slotName: card.slotName, gradeName: card.gradeName, color: card.color, ilvl: item.ilvl,
      stats: card.stats, affixes: card.affixes, sockets: card.sockets, socketCodes: socketsOf(item), desc: o.desc, price, owned, compare, can: reason === '', reason,
      icon: townGearIconPath(o.town, o.slot, hero),
    };
  });
}

/** 상점 안내 한 줄: 드롭보다 약한 같은 레벨 희귀 상당 · 이전 마을 품목은 워프 */
export function townGearNote(town: number): string {
  return `${TOWN_NAME[town] ?? '이 마을'} 단계 장비(아이템 레벨 ${townGearLevel(town)} · 희귀 · 옵션 1줄). 사냥에서 얻는 같은 지역 장비보다 약하게 맞춰 두었어요. 이전 마을 장비는 워프로 그 마을에 가서 살 수 있어요.`;
}

/**
 * 「마을 장비」 탭 본문. isNew = 이 마을에 처음 와서 아직 이 탭을 본 적 없음(품목마다 「신규」 표시).
 * 장비 통합(ECONOMY 14절) 뒤에는 마을①부터 모든 마을이 이 탭을 쓴다(옛 무기·갑옷 상점은 닫혔다).
 * 2026-10-27: 품목마다 아이콘 카드(왼쪽 큰 그림 + 등급 테두리, 가운데 이름·능력치·소켓 그림 칸, 오른쪽 금화 가격 + 구매).
 * bodyW(설계 px)가 좁으면 가격·구매를 카드 아래 줄로 내린다(shopLayout) — 오른쪽 아래 대화 단추에 가리지 않는다
 */
export function renderTownShop(body: HTMLElement, rows: readonly TownGearRow[], town: number, isNew: boolean, send: (c: GameCommand) => void, bodyW = DEFAULT_BODY_W): void {
  const root = el('div', '', body);
  root.className = 'dh-g';
  root.dataset.testid = 'town-gear';
  const h = el('div', 'display:flex;align-items:center;gap:8px;', root);
  const crest = townCrest(town);
  if (crest) iconOver(el('div', 'flex:0 0 auto;width:30px;height:30px;', h), crest, 34, '');
  const ht = el('div', 'flex:1;min-width:0;', h);
  el('div', 'font:900 16px ' + FONT + ';color:#ffd76a;letter-spacing:.4px;', ht, (TOWN_NAME[town] ?? '마을') + ' 장비');
  el('div', '', ht, townGearNote(town)).className = 'dh-g-note';
  const list = el('div', '', root);
  list.className = 'dh-g-list';
  list.style.maxHeight = 'none';
  const lay = shopRowLayout(bodyW);
  for (const r of rows) {
    const card = el('div', '--gc:' + r.color + ';display:grid;grid-template-columns:' + lay.columns + ';gap:6px 10px;align-items:start;', list);
    card.className = 'dh-g-card';
    card.dataset.testid = 'town-gear-' + r.id;
    card.dataset.layout = lay.stack ? 'stack' : 'side';
    // 품목 그림(마을 테마별 투구·갑옷·무기, 등급색 테두리) + 아이템 레벨 칸 배지
    const pic = itemIcon(card, r.icon, lay.icon, r.color, false);
    if (pic) cornerTag(pic, 'Lv ' + r.ilvl, 'br');
    else el('div', '', card);
    const left = el('div', 'min-width:0;display:flex;flex-direction:column;gap:2px;', card);
    const nm = el('div', 'display:flex;align-items:center;gap:6px;flex-wrap:wrap;', left);
    el('span', '', nm, r.name).className = 'nm';
    if (isNew) {
      const b = el('span', 'flex:0 0 auto;padding:1px 7px;border-radius:9px;background:linear-gradient(180deg,#ff5a5a,#c81e2e);color:#fff;font:900 11px ' + FONT + ';box-shadow:0 0 6px rgba(255,80,80,.6);', nm, '신규');
      b.dataset.testid = 'town-gear-new';
    }
    el('div', '', left, r.gradeName + ' ' + r.slotName + ' · 아이템 레벨 ' + r.ilvl + (r.owned > 0 ? ' · 보유 ' + r.owned : '')).className = 'sub';
    el('div', '', left, r.stats.join(' · ')).className = 'st';
    for (const a of r.affixes) el('div', '', left, a).className = 'af';
    if (r.socketCodes.length > 0) {
      // 소켓 그림 칸: 박힌 보석은 보석 그림, 빈 칸은 빈 소켓 그림
      const sk = el('div', 'display:flex;align-items:center;gap:5px;margin-top:2px;', left);
      sk.dataset.testid = 'town-gear-sockets-' + r.id;
      sk.setAttribute('aria-label', r.sockets);
      for (const code of r.socketCodes) socketPip(sk, code, 26);
      el('span', 'font:700 11.5px ' + FONT + ';color:#9ff0ff;margin-left:2px;', sk, '소켓 ' + r.socketCodes.length);
    }
    el('div', 'font:700 11.5px ' + FONT + ';color:#cfd8f2;', left, r.compare);
    el('div', '', left, r.desc).className = 'dh-g-note';
    // 가격·구매: 가로 배치면 오른쪽 열, 좁으면 카드 아래 줄(가격 왼쪽·단추 오른쪽)
    const right = el('div', lay.stack
      ? 'grid-column:1/-1;display:flex;align-items:center;justify-content:space-between;gap:10px;flex-wrap:wrap;'
      : 'display:flex;flex-direction:column;align-items:stretch;gap:6px;', card);
    right.dataset.testid = 'town-gear-buyrow-' + r.id;
    const pr = priceTag(right, r.price, 24, '#ffd76a', 'town-gear-price-' + r.id);
    pr.style.justifyContent = lay.stack ? 'flex-start' : 'flex-end';
    const act = el('div', lay.stack ? 'display:flex;flex-direction:column;align-items:flex-end;gap:4px;flex:1 1 auto;' : 'display:flex;flex-direction:column;align-items:stretch;gap:4px;', right);
    const b = el('button', 'min-width:96px;' + (lay.stack ? 'align-self:flex-end;' : ''), act, '구매');
    b.type = 'button';
    b.className = 'dh-g-btn' + (r.can ? ' pri' : '');
    b.dataset.sfx = 'event';
    b.dataset.testid = 'town-gear-buy-' + r.id;
    b.dataset.focusKey = 'town-gear-buy-' + r.id;
    b.disabled = !r.can;
    b.addEventListener('click', () => send({ type: 'gearBuy', offer: r.id, requestId: makeRequestId() }));
    if (r.reason) el('div', 'font:800 11px ' + FONT + ';color:#ffb37a;text-align:' + (lay.stack ? 'right' : 'center') + ';', act, r.reason);
  }
}
